/**
 * @file useDdcAllocation.js
 * @description DDC controller allocation and I/O management
 *
 * Handles:
 * - DDC controller allocation to equipment/devices
 * - I/O capacity calculations
 * - Available controller filtering
 * - Deallocation operations
 */

import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';

export function useDdcAllocation() {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * DDC allocation dialog visibility
   * @type {Ref<boolean>}
   */
  const ddcAllocationDialogVisible = ref(false);

  /**
   * Current entity being allocated
   * @type {Ref<Object|null>}
   */
  const ddcAllocationEntity = ref(null);

  /**
   * Available DDC controllers for allocation
   * @type {Ref<Array>}
   */
  const availableDdcControllers = ref([]);

  /**
   * Selected DDC controller for allocation
   * @type {Ref<number|null>}
   */
  const selectedDdcController = ref(null);

  /**
   * Loading state for allocation operations
   * @type {Ref<boolean>}
   */
  const allocatingDdc = ref(false);

  /**
   * I/O requirement for current entity
   * @type {Ref<Object>}
   */
  const ioRequirement = ref({
    analog_inputs: 0,
    analog_outputs: 0,
    binary_inputs: 0,
    binary_outputs: 0,
  });

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Calculate total I/O requirements
   */
  const totalIoRequired = computed(() => {
    return (
      ioRequirement.value.analog_inputs +
      ioRequirement.value.analog_outputs +
      ioRequirement.value.binary_inputs +
      ioRequirement.value.binary_outputs
    );
  });

  /**
   * Get available DDC controllers with capacity info
   */
  const ddcControllersWithCapacity = computed(() => {
    return availableDdcControllers.value.map((controller) => {
      const allocated = calculateAllocatedIO(controller);
      const capacity = {
        analog_inputs: controller.analog_input_capacity || 0,
        analog_outputs: controller.analog_output_capacity || 0,
        binary_inputs: controller.binary_input_capacity || 0,
        binary_outputs: controller.binary_output_capacity || 0,
      };

      const remaining = {
        analog_inputs: capacity.analog_inputs - allocated.analog_inputs,
        analog_outputs: capacity.analog_outputs - allocated.analog_outputs,
        binary_inputs: capacity.binary_inputs - allocated.binary_inputs,
        binary_outputs: capacity.binary_outputs - allocated.binary_outputs,
      };

      const hasCapacity =
        remaining.analog_inputs >= ioRequirement.value.analog_inputs &&
        remaining.analog_outputs >= ioRequirement.value.analog_outputs &&
        remaining.binary_inputs >= ioRequirement.value.binary_inputs &&
        remaining.binary_outputs >= ioRequirement.value.binary_outputs;

      return {
        ...controller,
        allocated,
        capacity,
        remaining,
        hasCapacity,
        utilizationPercent: calculateUtilizationPercent(allocated, capacity),
      };
    });
  });

  /**
   * Filter controllers that have sufficient capacity
   */
  const compatibleControllers = computed(() => {
    return ddcControllersWithCapacity.value.filter((c) => c.hasCapacity);
  });

  // ============================================================================
  // METHODS
  // ============================================================================

  /**
   * Open DDC allocation dialog for entity
   * @param {Object} entity - Equipment or device entity
   * @param {Array} ddcControllers - Available DDC controllers
   */
  function openDdcAllocationDialog(entity, ddcControllers) {

    ddcAllocationEntity.value = entity;
    availableDdcControllers.value = ddcControllers;
    selectedDdcController.value = entity.ddc_controller_id || null;

    // Calculate I/O requirements from entity's points
    ioRequirement.value = calculateIoRequirement(entity);

    ddcAllocationDialogVisible.value = true;
  }

  /**
   * Close DDC allocation dialog
   */
  function closeDdcAllocationDialog() {
    ddcAllocationDialogVisible.value = false;
    ddcAllocationEntity.value = null;
    selectedDdcController.value = null;
    ioRequirement.value = {
      analog_inputs: 0,
      analog_outputs: 0,
      binary_inputs: 0,
      binary_outputs: 0,
    };
  }

  /**
   * Calculate I/O requirements for entity based on its points
   * @param {Object} entity - Equipment or device with points
   * @returns {Object} I/O requirements
   */
  function calculateIoRequirement(entity) {
    const requirement = {
      analog_inputs: 0,
      analog_outputs: 0,
      binary_inputs: 0,
      binary_outputs: 0,
    };

    if (!entity.points || !Array.isArray(entity.points)) {
      return requirement;
    }

    entity.points.forEach((point) => {
      const pointClass = point.brick_class || '';
      const isWritable = pointClass.includes('Command') || pointClass.includes('Setpoint');

      if (pointClass.includes('Sensor') || pointClass.includes('Status')) {
        // Input point
        if (isAnalogPoint(pointClass)) {
          requirement.analog_inputs++;
        } else {
          requirement.binary_inputs++;
        }
      } else if (isWritable) {
        // Output point
        if (isAnalogPoint(pointClass)) {
          requirement.analog_outputs++;
        } else {
          requirement.binary_outputs++;
        }
      }
    });

    return requirement;
  }

  /**
   * Check if point class is analog (vs binary/digital)
   * @param {string} brickClass - BRICK class of point
   * @returns {boolean}
   */
  function isAnalogPoint(brickClass) {
    const analogKeywords = [
      'Temperature',
      'Pressure',
      'Flow',
      'Speed',
      'Level',
      'Position',
      'Humidity',
      'CO2',
      'Voltage',
      'Current',
      'Power',
      'Frequency',
    ];

    return analogKeywords.some((keyword) => brickClass.includes(keyword));
  }

  /**
   * Calculate allocated I/O for a DDC controller
   * @param {Object} controller - DDC controller
   * @returns {Object} Allocated I/O counts
   */
  function calculateAllocatedIO(controller) {
    const allocated = {
      analog_inputs: 0,
      analog_outputs: 0,
      binary_inputs: 0,
      binary_outputs: 0,
    };

    if (!controller.allocated_equipment || !Array.isArray(controller.allocated_equipment)) {
      return allocated;
    }

    controller.allocated_equipment.forEach((equipment) => {
      if (equipment.points) {
        const requirement = calculateIoRequirement(equipment);
        allocated.analog_inputs += requirement.analog_inputs;
        allocated.analog_outputs += requirement.analog_outputs;
        allocated.binary_inputs += requirement.binary_inputs;
        allocated.binary_outputs += requirement.binary_outputs;
      }
    });

    return allocated;
  }

  /**
   * Calculate utilization percentage for controller
   * @param {Object} allocated - Allocated I/O
   * @param {Object} capacity - Total capacity
   * @returns {number} Utilization percentage (0-100)
   */
  function calculateUtilizationPercent(allocated, capacity) {
    const totalAllocated =
      allocated.analog_inputs +
      allocated.analog_outputs +
      allocated.binary_inputs +
      allocated.binary_outputs;

    const totalCapacity =
      capacity.analog_inputs +
      capacity.analog_outputs +
      capacity.binary_inputs +
      capacity.binary_outputs;

    if (totalCapacity === 0) {return 0;}

    return Math.round((totalAllocated / totalCapacity) * 100);
  }

  /**
   * Save DDC allocation
   * @param {Function} apiCallback - Async function to save allocation via API
   * @returns {Promise<Object>} Updated entity
   */
  async function saveDdcAllocation(apiCallback) {
    if (!selectedDdcController.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a DDC controller',
      });
      return null;
    }

    // Check capacity
    const controller = ddcControllersWithCapacity.value.find(
      (c) => c.id === selectedDdcController.value,
    );

    if (!controller?.hasCapacity) {
      $q.notify({
        type: 'negative',
        message: 'Selected controller does not have sufficient I/O capacity',
      });
      return null;
    }

    try {
      allocatingDdc.value = true;

      const result = await apiCallback({
        entity_id: ddcAllocationEntity.value.id,
        entity_type: ddcAllocationEntity.value.type,
        ddc_controller_id: selectedDdcController.value,
      });

      $q.notify({
        type: 'positive',
        message: 'DDC controller allocated successfully',
      });

      closeDdcAllocationDialog();
      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to allocate DDC controller: ${error.message}`,
      });
      throw error;
    } finally {
      allocatingDdc.value = false;
    }
  }

  /**
   * Deallocate DDC controller from entity
   * @param {Object} entity - Entity to deallocate
   * @param {Function} apiCallback - Async function to deallocate via API
   * @returns {Promise<Object>} Updated entity
   */
  async function deallocateDdcController(entity, apiCallback) {
    if (!entity.ddc_controller_id) {
      $q.notify({
        type: 'warning',
        message: 'Entity is not allocated to any DDC controller',
      });
      return null;
    }

    try {
      const confirmed = await new Promise((resolve) => {
        $q.dialog({
          title: 'Confirm Deallocation',
          message: `Remove ${entity.name} from DDC controller?`,
          cancel: true,
          persistent: false,
        })
          .onOk(() => resolve(true))
          .onCancel(() => resolve(false));
      });

      if (!confirmed) {return null;}

      allocatingDdc.value = true;

      const result = await apiCallback({
        entity_id: entity.id,
        entity_type: entity.type,
      });

      $q.notify({
        type: 'positive',
        message: 'DDC controller deallocated successfully',
      });

      return result;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to deallocate: ${error.message}`,
      });
      throw error;
    } finally {
      allocatingDdc.value = false;
    }
  }

  /**
   * Get DDC controller capacity summary string
   * @param {Object} controller - Controller with capacity info
   * @returns {string} Human-readable capacity summary
   */
  function getCapacitySummary(controller) {
    if (!controller.remaining) {return '';}

    const parts = [];

    if (controller.capacity.analog_inputs > 0) {
      parts.push(
        `AI: ${controller.remaining.analog_inputs}/${controller.capacity.analog_inputs}`,
      );
    }

    if (controller.capacity.analog_outputs > 0) {
      parts.push(
        `AO: ${controller.remaining.analog_outputs}/${controller.capacity.analog_outputs}`,
      );
    }

    if (controller.capacity.binary_inputs > 0) {
      parts.push(
        `BI: ${controller.remaining.binary_inputs}/${controller.capacity.binary_inputs}`,
      );
    }

    if (controller.capacity.binary_outputs > 0) {
      parts.push(
        `BO: ${controller.remaining.binary_outputs}/${controller.capacity.binary_outputs}`,
      );
    }

    return parts.join(' | ');
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    ddcAllocationDialogVisible,
    ddcAllocationEntity,
    availableDdcControllers,
    selectedDdcController,
    allocatingDdc,
    ioRequirement,

    // Computed
    totalIoRequired,
    ddcControllersWithCapacity,
    compatibleControllers,

    // Methods
    openDdcAllocationDialog,
    closeDdcAllocationDialog,
    calculateIoRequirement,
    isAnalogPoint,
    calculateAllocatedIO,
    calculateUtilizationPercent,
    saveDdcAllocation,
    deallocateDdcController,
    getCapacitySummary,
  };
}
